import Alpine from "alpinejs";

/**
 * @param surface {'horizontal' | 'vertical'},
 * @param gap {Number} in rems,
 * @param isClickable {Boolean}
 * (could be provided via data-scrollable attribute)
 */

class Scrollable {
    static classes = {
        node: {
            arrowsHolder: 'scrollable-arrows',
            arrow: {
                top: 'scrollable-arrows__item-top',
                bottom: 'scrollable-arrows__item-bottom',
                right: 'scrollable-arrows__item-right',
                left: 'scrollable-arrows__item-left',
            }
        },
        style: {
            arrowsHolder: 'pointer-events-none absolute inset-0 flex',
        }
    }

    constructor(element, options) {
        if (!element) return;
        this.element = element;

        let isHorizontalScrollable = this.element.scrollWidth > this.element.clientWidth;
        let isVerticalScrollable = this.element.scrollHeight > this.element.clientHeight;

        if (!isHorizontalScrollable && !isVerticalScrollable) return;

        // use data-attribute options if non provided
        if (!options) {
            options = JSON.parse(this.element.dataset.scrollable);
        }

        if (!options) return;

        this.options = {
            surface: options.surface || 'horizontal',
            direction: options.direction || 'both',
            gap: options.gap ? (parseInt(options.gap) || 0) : 0, //number (px)
            isClickable: options.isClickable || false
        }

        this.isHorizontal = this.options.surface === 'horizontal';

        this.isScrollableHorizontal = this.element.scrollWidth > this.element.clientWidth;
        this.isScrollableVertical = this.element.scrollHeight > this.element.clientHeight;

        this.element.append(this.createArrowElements());

        this.element.addEventListener('scroll', () => {
            if (this.isHorizontal) {
                this.isScrolledToTheEnd = this.element.scrollLeft + this.element.clientWidth + this.options.gap >= this.element.scrollWidth;
                this.isScrolledToTheStart = this.element.scrollLeft <= this.options.gap;
            } else {
                this.isScrolledToTheEnd = this.element.scrollTop + this.element.clientHeight + this.options.gap >= this.element.scrollHeight;
                this.isScrolledToTheStart = this.element.scrollTop <= this.options.gap;
            }

            this.toggleArrows();
        });
    }

    createArrowElements() {
        let arrowsHolder = document.createElement('DIV');

        // create arrows holder tag depending on parent tag
        switch (this.element.tagName) {
            case 'UL':
                arrowsHolder = document.createElement('LI');
                break;
            case 'OL':
                arrowsHolder = document.createElement('LI');
                break;
            case 'DL':
                arrowsHolder = document.createElement('DT');
                break;
        }

        // add classes
        //
        let justifyClass = `justify-${this.isHorizontal ? 'center' : this.options.direction === 'prev' ? 'start' : this.options.direction === 'next' ? 'end' : 'center'}`;
        let alignClass = `align-${this.isHorizontal ? this.options.direction === 'prev' ? 'start' : this.options.direction === 'next' ? 'end' : 'center' : 'center'}`;
        arrowsHolder.classList.add(...Scrollable.classes.style.arrowsHolder.split(' '));
        arrowsHolder.classList.add(justifyClass, alignClass);
        arrowsHolder.classList.add(Scrollable.classes.node.arrowsHolder, this.isHorizontal ? 'flex-row' : 'flex-col');
        arrowsHolder.classList.add(Scrollable.classes.node.arrowsHolder, `p${this.isHorizontal ? 'x' : 'y'}-[${this.options.gap}px]`);

        // create arrows
        switch (this.options.direction) {
            case 'both':
                this.arrowNext = new ScrollableArrow('prev', this);
                this.arrowNext = new ScrollableArrow('next', this);
                break;
            case 'prev':
                this.arrowNext = new ScrollableArrow('prev', this);
                break;
            case 'next':
                this.arrowNext = new ScrollableArrow('next', this);
                break;
        }

        if (this.options.direction === 'both') {
            this.arrowPrev = new ScrollableArrow('prev', this);
            this.arrowNext = new ScrollableArrow('next', this);
            this.arrowPrev.hide();
        } else {
            this.arrowNext = new ScrollableArrow(this.options.direction, this);
        }

        // append arrows to holder
        if (this.arrowPrev) {
            arrowsHolder.append(this.arrowPrev.getElement());
        }
        if (this.arrowNext) {
            arrowsHolder.append(this.arrowNext.getElement());
        }

        return arrowsHolder;
    }

    toggleArrows() {
        if (this.arrowPrev) {
            this.arrowPrev[this.isScrolledToTheStart ? 'hide' : 'show']();
        }
        if (this.arrowNext) {
            this.arrowNext[this.isScrolledToTheEnd ? 'hide' : 'show']();
        }
    }

    convertRemToPixels(rem) {
        return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
    }
}

class ScrollableArrow {
    static arrowButtonHeight = 40;

    static classes = {
        node: {
            arrow: 'scrollable-arrows__item',
        },
        style: {
            arrow: 'flex justify-center align-center pointer-events-auto color-white opacity-1 transition-all duration-300 from-[primary] to-transparent',
            top: `mt-[-${ScrollableArrow.arrowButtonHeight}px] h-[${ScrollableArrow.arrowButtonHeight}px] bg-gradient-to-b`,
            bottom: `mb-[-${ScrollableArrow.arrowButtonHeight}px] h-[${ScrollableArrow.arrowButtonHeight}px] bg-gradient-to-t`,
            right: `mr-[-${ScrollableArrow.arrowButtonHeight}px] h-[${ScrollableArrow.arrowButtonHeight}px] bg-gradient-to-l`,
            left: `ml-[-${ScrollableArrow.arrowButtonHeight}px] h-[${ScrollableArrow.arrowButtonHeight}px] bg-gradient-to-r`,
        }
    }

    constructor(direction, parentInstance) {
        this.parentInstance = typeof parentInstance === 'function' ? parentInstance() : parentInstance;
        this.options = {
            direction: direction || 'next',
            isClickable: this.parentInstance.options.isClickable,
        };
        this.modifierClass = ScrollableArrow.classes.node.arrow + '--is-' + this.options.direction.toLowerCase();

        this.element = document.createElement('BUTTON');
        this.element.classList.add(...ScrollableArrow.classes.style.arrow.split(' '));
        this.element.setAttribute('type', 'button');
        this.element.setAttribute('aria-label', 'Scroll the full content for sighted users but the full content is already there for screen readers');

        let scrollOptions = {top: 0, left: 0, behavior: 'smooth'}

        if (this.options.direction === 'prev') {
            this.element.classList.add(...(this.parentInstance.isHorizontal ? ScrollableArrow.classes.style.left + ' h-full' : ScrollableArrow.classes.style.top + ' w-full').split(' '), this.modifierClass);
            this.element.innerHTML = `<svg class="w-[${ScrollableArrow.arrowButtonHeight}px] h-auto" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" ${this.parentInstance.isHorizontal ? '' : 'style="transform: rotate(90deg)"'}><path fill="#fff" d="M15.41 16.09l-4.58-4.59 4.58-4.59-1.41-1.41-6 6 6 6z"/></svg>`;
        }

        if (this.options.direction === 'next') {
            this.element.classList.add(...(this.parentInstance.isHorizontal ? ScrollableArrow.classes.style.right + ' h-full' : ScrollableArrow.classes.style.bottom + ' w-full').split(' '), this.modifierClass);
            this.element.innerHTML = `<svg class="w-[${ScrollableArrow.arrowButtonHeight}px] h-auto" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" ${this.parentInstance.isHorizontal ? '' : 'style="transform: rotate(90deg)"'}><path fill="#fff" d="M8.59 7.91l4.58 4.59-4.58 4.59 1.41 1.41 6-6-6-6z"/></svg>`;
        }

        if (!this.options.isClickable) return;

        this.element.addEventListener('click', () => {
            scrollOptions = {
                top: this.options.direction === 'prev' ? 0 : this.parentInstance.element.scrollHeight || 0,
                left: this.options.direction === 'prev' ? 0 : this.parentInstance.element.scrollWidth || 0,
                behavior: 'smooth'
            };
            this.parentInstance.element.scrollTo(scrollOptions);
        });
    }

    getElement() {
        return this.element;
    }

    show() {
        this.element.classList.remove('opacity-0', 'pointer-events-none');
        this.element.classList.add('pointer-events-auto');
    }

    hide() {
        this.element.classList.add('opacity-0', 'pointer-events-none');
        this.element.classList.remove('pointer-events-auto');
    }
}

Alpine.directive('scrollable', (el) => {
    // init only after hubspot form is loaded
    window.addEventListener('message', event => {
        if (event.data.type === 'hsFormCallback' && event.data.eventName === 'onFormReady') {
            new Scrollable(el);
        }
    });
});
